// Shared renderer for ONE streamed assistant turn — the assistant's markdown
// answer, its extended-thinking trace, and a tool-call trace (each call shows
// its permission decision + result). Extracted from AgentConsolePage so the
// embedded CPLEX Modeler chat panel renders turns identically.
//
// All inputs are plain data (AssistantBlock[] / UsageEvent / CurrentPhase) +
// the pure helpers in services/agentConsole; the components hold no state beyond
// the local "show trace" toggle.

import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ToolCallTrace } from './ToolCallTrace';
import FeedbackControls from './FeedbackControls';
import {
  extractCurrentPhase,
  formatCostUsd,
  formatTokens,
  splitFinalAnswer,
  type AssistantBlock,
  type CurrentPhase,
  type FeedbackContext,
  type UsageEvent,
} from '../../services/agentConsole';
import { useIsAdmin } from '../../context/AuthContext';
import { friendlyToolLabel } from '../../services/toolLabels';
import styles from './AssistantTurnView.module.css';

export function AssistantMarkdown({ content }: { content: string }) {
  return (
    <div className={styles.markdownContent}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Answer links point at external resources (chemmasters.net product
          // pages, published TDS/SDS PDFs) — open them in a new tab so the
          // chat conversation is never navigated away from.
          a: ({ node: _node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer" />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Render a sequence of assistant blocks in the order the model emitted them.
 * Used both for the "final answer" body and (when toggled open) for the full
 * trace of intermediate work.
 */
export function AssistantBlocks({ blocks }: { blocks: AssistantBlock[] }) {
  const isAdmin = useIsAdmin();
  return (
    <>
      {blocks.map((block, idx) => {
        if (block.kind === 'text') {
          return <AssistantMarkdown key={`b-${idx}`} content={block.text} />;
        }
        if (block.kind === 'thinking') {
          // The raw chain-of-thought is admin-only; team members see only the
          // plain-language tool steps (rendered by ToolCallTrace below).
          if (!isAdmin) return null;
          return (
            <div key={`b-${idx}`} className={styles.traceThinking}>
              {block.text}
            </div>
          );
        }
        return (
          <div key={`b-${idx}`} className={styles.toolRow}>
            <ToolCallTrace tool={block.tool} />
          </div>
        );
      })}
    </>
  );
}

/**
 * The "status replace" view shown while the agent is still iterating. Only the
 * latest narration (text or thinking) and the tool calls under it are visible;
 * each new narration replaces the previous phase, so the bubble doesn't
 * accumulate into a wall.
 */
export function CurrentPhasePanel({ phase }: { phase: CurrentPhase }) {
  const isAdmin = useIsAdmin();
  if (!phase.narration && phase.tools.length === 0) return null;
  return (
    <div className={styles.currentPhase}>
      {phase.narration && phase.narration.kind === 'text' && (
        <p className={styles.phaseNarration}>{phase.narration.text}</p>
      )}
      {/* Raw reasoning narration is admin-only; team members see the tool steps. */}
      {phase.narration && phase.narration.kind === 'thinking' && isAdmin && (
        <p className={`${styles.phaseNarration} ${styles.phaseThinking}`}>
          <em>{phase.narration.text}</em>
        </p>
      )}
      {/* Team members get a plain status while the model reasons, so the turn
          visibly moves from the first streamed thought instead of sitting blank
          until the first tool call (a large-context first step runs 17-35 s). */}
      {phase.narration && phase.narration.kind === 'thinking' && !isAdmin && phase.tools.length === 0 && (
        <p className={`${styles.phaseNarration} ${styles.phaseThinking}`} data-testid="phase-thinking-status">
          <em>Thinking it through…</em>
        </p>
      )}
      {phase.tools.map((tool, i) => (
        <div key={`phase-tool-${i}`} className={styles.toolRow}>
          <ToolCallTrace tool={tool} />
        </div>
      ))}
    </div>
  );
}

/**
 * One assistant turn. Default: just the final answer. While the model is still
 * iterating (final answer not yet streamed), show the live status phase only.
 * A "View thinking trace" button surfaces the intermediate narration + tool
 * sequence on demand.
 */
export function AssistantTurn({
  blocks,
  isStreaming,
  feedback,
}: {
  blocks: AssistantBlock[];
  isStreaming: boolean;
  // When supplied (and the turn has settled), render the shared 👍/👎 + comment
  // control under the answer. Omit it (or pass null) to render no feedback — so
  // surfaces that don't opt in (e.g. the embedded CPLEX panel) are unchanged.
  feedback?: FeedbackContext | null;
}) {
  const isAdmin = useIsAdmin();
  const [traceOpen, setTraceOpen] = useState(false);
  const { intermediate, finalAnswer } = splitFinalAnswer(blocks);

  // Still working: show the rolling status phase only.
  if (isStreaming && finalAnswer.length === 0) {
    return <CurrentPhasePanel phase={extractCurrentPhase(intermediate)} />;
  }

  // For non-admins the trace renders only plain-language tool steps (raw
  // thinking + internal-plumbing tools are hidden), so the toggle should appear
  // only when there is actually something a team member can see.
  const visibleCount = isAdmin
    ? intermediate.length
    : intermediate.filter((b) => b.kind === 'tool' && friendlyToolLabel(b.tool) !== null).length;
  const traceLabel = isAdmin
    ? traceOpen
      ? 'Hide thinking trace'
      : 'View thinking trace'
    : traceOpen
      ? 'Hide the steps'
      : 'See the steps';

  return (
    <>
      <AssistantBlocks blocks={finalAnswer} />
      {visibleCount > 0 && (
        <button
          type="button"
          className={styles.traceToggle}
          onClick={() => setTraceOpen((v) => !v)}
        >
          {traceLabel}
        </button>
      )}
      {traceOpen && visibleCount > 0 && (
        <div className={styles.traceContainer}>
          <AssistantBlocks blocks={intermediate} />
        </div>
      )}
      {/* Feedback shows once the answer has settled (not mid-stream) and the
          caller supplied a real audit message id to attach it to. */}
      {feedback && !isStreaming && finalAnswer.length > 0 && (
        <FeedbackControls context={feedback} />
      )}
    </>
  );
}

export function UsageMeta({ usage }: { usage: UsageEvent }) {
  const parts: string[] = [];
  const tokens = formatTokens(usage);
  if (tokens) parts.push(tokens);
  if (usage.cost_usd != null) parts.push(formatCostUsd(usage.cost_usd));
  if (usage.stop_reason) parts.push(usage.stop_reason);
  if (parts.length === 0) return null;
  return <div className={styles.meta}>{parts.join(' · ')}</div>;
}
